import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Fingerprint, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  biometricSupported, biometricAvailable, biometricVerify,
  isBiometricEnabled, setBiometricEnabled,
} from "../lib/biometric";

// Full-screen lock shown on the native app when biometric unlock is enabled.
export function BiometricLock({ onUnlocked }) {
  const { logout } = useAuth();
  const [error, setError] = useState(false);

  const attempt = useCallback(async () => {
    setError(false);
    try { await biometricVerify("Unlock your patient portal"); onUnlocked(); }
    catch { setError(true); }
  }, [onUnlocked]);

  useEffect(() => { attempt(); }, [attempt]);

  return (
    <div className="min-h-screen bg-clinical text-white flex flex-col items-center justify-center px-6 text-center" data-testid="biometric-lock">
      <Fingerprint className="w-16 h-16 text-alert mb-6" />
      <h1 className="font-heading font-black text-2xl">Portal locked</h1>
      <p className="text-white/70 mt-2 max-w-xs text-sm">Verify your identity to view your appointments.</p>
      <button onClick={attempt} data-testid="biometric-unlock-btn" className="mt-8 inline-flex items-center gap-2 bg-white text-clinical font-semibold rounded-md px-6 py-3">
        <Fingerprint className="w-5 h-5" /> {error ? "Try again" : "Unlock"}
      </button>
      <button onClick={logout} data-testid="biometric-signout-btn" className="mt-4 inline-flex items-center gap-1.5 text-white/70 text-sm">
        <LogOut className="w-4 h-4" /> Sign out instead
      </button>
    </div>
  );
}

// Opt-in toggle, rendered only on the native app when biometry is available.
export function BiometricToggle() {
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(isBiometricEnabled());

  useEffect(() => { (async () => setAvailable(await biometricAvailable()))(); }, []);

  if (!biometricSupported() || !available) return null;

  const toggle = async () => {
    if (enabled) { setBiometricEnabled(false); setEnabled(false); toast("Biometric unlock disabled"); return; }
    try {
      await biometricVerify("Confirm to enable quick unlock");
      setBiometricEnabled(true); setEnabled(true); toast.success("Biometric unlock enabled");
    } catch { toast.error("Could not enable biometric unlock"); }
  };

  return (
    <button onClick={toggle} data-testid="biometric-toggle" className={`mt-4 inline-flex items-center gap-2 text-sm font-semibold rounded-md px-4 py-2 border transition-colors ${enabled ? "bg-calm/10 border-calm text-calm" : "border-line text-muted2 hover:bg-line/40"}`}>
      <Fingerprint className="w-4 h-4" /> {enabled ? "Quick unlock enabled" : "Enable Face ID / fingerprint unlock"}
    </button>
  );
}
